import React, { useContext, useState } from "react";
import { useCookies } from "react-cookie";
import styled from "styled-components";
import ReactHtmlParser from "react-html-parser";
import { Modal } from "notes";
import { RegistrationForm } from "./Components";
import { Layout } from "../Layout";
import { Duration } from "luxon";
import {
  EventContext,
  TimeContext,
  AnalyticsContext,
  PoweredBy,
} from "Components";
import { fonts } from "theme/fonts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlay as playIcon } from "@fortawesome/free-solid-svg-icons";

export const RegistrationLayout = React.memo(({ onSuccess, postCampaign }) => {
  const { event } = useContext(EventContext);
  const { time } = useContext(TimeContext);
  const { logClick, logVideoAction } = useContext(AnalyticsContext);
  const [cookies, setCookie] = useCookies();

  const [open, setOpen] = useState(false);
  const [video, setVideo] = useState(null);

  const api = process.env.REACT_APP_FIRE_BASE_STORAGE_API;
  const storageDirectory = event.storageDirectory;

  let desktopImage = `${api}/o/${storageDirectory}%2F${event?.register?.artistImage?.desktop}?alt=media`;
  let mobileImage = `${api}/o/${storageDirectory}%2F${event?.register?.artistImage?.mobile}?alt=media`;

  const { 0: brand, 1: secondary } = event.logos;
  let brandLogo = `${api}/o/${storageDirectory}%2F${brand.path}?alt=media`;
  let secondaryLogo = `${api}/o/${storageDirectory}%2F${secondary.path}?alt=media`;

  const handleSuccess = (admin, token, formPart) => {
    onSuccess(admin, token, formPart);
  };

  const setModalVisible = (bool, video) => {
    setVideo(video);
    setOpen(bool);
    logVideoAction({
      bool,
      videoLink: video,
      videoTitle: "Hero Video",
    });
  };

  const logoClickHandler = (link) => {
    logClick({
      label: "Registration Logo",
      url: link,
    });
  };

  const confirmationClickHandler = (link) => {
    logClick({
      label: "Confirmation Button",
      url: link,
    });
  };

  const brandCTAClickHandler = (url) => {
    logClick({
      label: "Primary CTA",
      url: url,
    });
  };

  const postSweepsCTAClickHandler = (link) => {
    logClick({
      label: "PostSweeps/PostCampaign CTA",
      url: link,
    });
  };

  let RegFormContent;
  const sweepsActive = event.endSweeps > time ? true : false;
  const sweepsPart = !!sweepsActive ? `sweeps` : `postSweeps`;
  const formPart = cookies.formPart ? parseInt(cookies.formPart) : 0;

  if (postCampaign) {
    // campaign has ended
    const { heading, body } = event.postCampaign;
    RegFormContent = (
      <>
        {heading && <Heading>{ReactHtmlParser(heading)}</Heading>}
        {body && <Body>{ReactHtmlParser(body)}</Body>}
        <PostCampaignCTA>
          <Heading>
            {ReactHtmlParser(event.callToActions.postCampaign.heading)}
          </Heading>
          <SubHeading>
            {ReactHtmlParser(event.callToActions.postCampaign.subHeading)}
          </SubHeading>
          <Body>{ReactHtmlParser(event.callToActions.postCampaign.body)}</Body>
          <ButtonContentWidth
            href={event.callToActions.postCampaign.link}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() =>
              postSweepsCTAClickHandler(event.callToActions.postCampaign.link)
            }
          >
            {event.callToActions.postCampaign.btnText}
          </ButtonContentWidth>
        </PostCampaignCTA>
        <PoweredBy />
      </>
    );
  } else {
    const { heading, body, submit, formLegal } = event.register[sweepsPart][
      formPart
    ];
    RegFormContent = (
      <>
        {heading && <Heading>{ReactHtmlParser(heading)}</Heading>}
        {body && <Body>{ReactHtmlParser(body)}</Body>}
        <RegistrationForm
          formPart={formPart}
          formLegal={formLegal}
          onSuccess={handleSuccess}
          submitButton={submit}
        />
        {formPart !== 0 && <PoweredBy />}
      </>
    );

    if (cookies?.registered) {
      const { heading, body, btnText: button, btnLink: link } = event.register[
        sweepsPart
      ][formPart];

      RegFormContent = (
        <>
          {heading && (
            <ConfirmationHeading>
              {ReactHtmlParser(heading)}
            </ConfirmationHeading>
          )}
          {body && <ConfirmationBody>{ReactHtmlParser(body)}</ConfirmationBody>}
          <PoweredBy />
        </>
      );
    }
  }

  const {
    button: brandButton,
    heading: brandHeading,
    link: brandLink,
    video: brandVideo,
  } = event.callToActions.brand;

  return (
    <Layout
      registration
      showHeader
      flexGrow
      showFooter
      setModalVisible={setModalVisible}
    >
      <Registration>
        <ContentWrapper>
          <Ticket>
            <TicketHeader>
              <Link
                href={brand.link}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => logoClickHandler(brand.link)}
              >
                <PrimaryLogo src={brandLogo} />
              </Link>
            </TicketHeader>
            <TicketBody>{RegFormContent}</TicketBody>
          </Ticket>
        </ContentWrapper>

        <ArtistImage
          as="section"
          id="artist-image"
          desktopImage={desktopImage}
          mobileImage={mobileImage}
        >
          <OverlayContainer>
            <OverlayContent>
              <PlayIcon onClick={() => setModalVisible(true, brandVideo)}>
                <StyledIcon icon={playIcon} />
              </PlayIcon>

              <BrandCTA>
                <BrandHeading>{ReactHtmlParser(brandHeading)}</BrandHeading>
                <BrandLink
                  href={brandLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => brandCTAClickHandler(brandLink)}
                >
                  {brandButton}
                </BrandLink>
              </BrandCTA>
            </OverlayContent>
          </OverlayContainer>
        </ArtistImage>
      </Registration>

      <VideoModal size="large" open={open} setOpen={setModalVisible}>
        <VideoModalContainer>
          <Video
            src={video}
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
          />
        </VideoModalContainer>
      </VideoModal>
    </Layout>
  );
});

// ======== CONTENT / LEFT SIDE ======== //

const Registration = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  flex: 1;

  @media only screen and ${(props) => props.theme.maxMedia.medium} {
    flex-direction: row;
  }
`;

const ContentWrapper = styled.div`
  background-color: #102b4e;
  box-shadow: 0 3px 6px rgb(0 0 0 / 20%);
  z-index: 1;
  margin-bottom: 15px;

  @media only screen and ${(props) => props.theme.maxMedia.medium} {
    margin: 0;
    padding: 0;
    width: 500px;
    min-height: 700px;
    margin-top: -146px;
  }
`;

const Ticket = styled.div`
  margin: 0 auto;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background-color: #ffffff;
  height: 100%;

  @media only screen and ${(props) => props.theme.maxMedia.medium} {
    margin: 0;
  }

  p {
    margin-top: 0;
  }
`;

const TicketHeader = styled.div`
  padding: 24px 15px;
  border-bottom: 1px solid rgb(16 43 78 / 50%);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TicketBody = styled.div`
  padding: 25px;
`;

const Link = styled.a``;

const Logo = styled.img`
  width: 100%;
  display: flex;
`;

const PrimaryLogo = styled(Logo)`
  width: 100%;
  max-width: 284px;
`;

const PostCampaignCTA = styled.div`
  border-top: 1px solid rgb(16 43 78 / 50%);
  margin-left: -25px;
  margin-right: -25px;
  padding: 25px;

  h2 {
    margin-bottom: 0;
  }

  h3 {
    font-size: 18px;
  }

  a {
    font-size: 18px;
  }
`;

const Heading = styled.h2`
  line-height: 1.25;
  margin-bottom: 16px;
  color: #4580dd;
  text-transform: uppercase;
`;

const Body = styled.p`
  font-size: 16px;
  color: #102b4e;
`;

const ConfirmationHeading = styled(Heading)``;
const ConfirmationBody = styled(Body)``;

const Button = styled.a`
  background-color: #4580dd;
  color: #ffffff;
  padding: 15px 35px;
  cursor: pointer;
  font-size: 20px;
  font-family: ${fonts.brand.light};
  margin-bottom: 18px;
  display: flex;
  justify-content: center;
  text-decoration: none;
  margin-top: 24px;
  text-align: center;

  &:hover,
  &:focus {
    background-color: #102b4e;
  }
`;

const ButtonContentWidth = styled(Button)`
  width: fit-content;
`;

const SubHeading = styled.h3`
  font-size: 20px;
`;

const PlayIcon = styled.div`
  width: 75px;
  height: 75px;
  border: 3px solid #ffffff;
  border-radius: 100px;
  padding: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: clamp(20px, 6vw, 40px);
  margin-bottom: 10%;
  cursor: pointer;

  @media only screen and ${(props) => props.theme.maxMedia.medium} {
    width: 100px;
    height: 100px;
    margin-bottom: 12px;
  }
`;

const StyledIcon = styled(FontAwesomeIcon)`
  color: #ffffff;
  margin-left: 4px;
  width: 100% !important;

  path {
    stroke: #ffffff;
    stroke-width: 30px;
  }
`;

// ======== ARTIST IMAGE / RIGHT SIDE ======== //

const ArtistImage = styled.div`
  position: relative;
  align-items: flex-start;
  background-image: url(${(props) => props.mobileImage});
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  padding-top: 56.25%;
  margin-bottom: 30px;

  @media only screen and ${(props) => props.theme.maxMedia.medium} {
    background-image: url(${(props) => props.desktopImage});
    flex: 1;
    padding-top: 0;
    margin: 0;
  }
`;

const OverlayContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 25px;

  @media only screen and ${(props) => props.theme.maxMedia.medium} {
    justify-content: flex-end;
    padding: 75px;
  }
`;

const OverlayContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #ffffff;
  width: 100%;
  height: 100%;
  justify-content: center;

  @media only screen and ${(props) => props.theme.maxMedia.medium} {
    max-width: 600px;
    justify-content: flex-end;
  }
`;

const BrandCTA = styled.div`
  position: absolute;
  bottom: -30px;
  padding: 0 25px;
  width: 100%;

  @media only screen and ${(props) => props.theme.maxMedia.medium} {
    position: relative;
    bottom: unset;
    padding: 0;
  }
`;

const BrandHeading = styled.h3`
  font-family: ${fonts.brand.condensedBold};
  text-transform: uppercase;
  font-size: clamp(20px, 5vw, 56px);
  margin-bottom: 12px;
  text-align: center;
  line-height: 1;
  text-shadow: 0 3px 15px rgba(0, 0, 0, 0.9);

  span {
    white-space: nowrap;
  }
`;

const BrandLink = styled(Button)`
  margin: 0;
  width: 100%;
`;

// ======== MODAL ======== //

const VideoModal = styled(Modal)`
  justify-content: center;
  min-height: unset;
  position: relative;

  svg {
    top: 10px;
    right: 10px;
  }
`;

const VideoModalContainer = styled.div`
  padding-top: 56.25%;
  position: relative;
`;

const Video = styled.iframe`
  height: 100%;
  position: absolute;
  top: 0;
  width: 100%;
  border: 0;
`;
